import { addMonths, setMonth, setYear, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

function MonthYearDropdowns({
	displayMonth,
	onMonthChange,
	onPreviousMonth,
	onNextMonth,
	startYear = 1920,
	endYear = 2080,
}: {
	displayMonth: Date;
	onMonthChange: (month: Date) => void;
	onPreviousMonth: () => void;
	onNextMonth: () => void;
	startYear?: number;
	endYear?: number;
}) {
	const years = React.useMemo(() => {
		const arr: number[] = [];
		for (let y = startYear; y <= endYear; y++) arr.push(y);
		return arr;
	}, [startYear, endYear]);

	return (
		<div className="flex w-full items-center justify-between">
			<button
				type="button"
				onClick={onPreviousMonth}
				className={cn(
					buttonVariants({ variant: "outline" }),
					"flex size-7 items-center justify-center bg-transparent opacity-50 hover:opacity-100",
				)}
			>
				<ChevronLeft className="size-4" />
			</button>
			<div className="flex items-center gap-1">
				<select
					value={displayMonth.getMonth()}
					onChange={(e) =>
						onMonthChange(setMonth(displayMonth, Number(e.target.value)))
					}
					className="h-7 rounded-(--radius-xs) border border-(--color-border) bg-(--color-card) px-1 font-medium text-sm focus:outline-none"
				>
					{MONTHS.map((m, i) => (
						<option key={m} value={i}>
							{m}
						</option>
					))}
				</select>
				<select
					value={displayMonth.getFullYear()}
					onChange={(e) =>
						onMonthChange(setYear(displayMonth, Number(e.target.value)))
					}
					className="h-7 rounded-(--radius-xs) border border-(--color-border) bg-(--color-card) px-1 font-medium text-sm focus:outline-none"
				>
					{years.map((y) => (
						<option key={y} value={y}>
							{y}
						</option>
					))}
				</select>
			</div>
			<button
				type="button"
				onClick={onNextMonth}
				className={cn(
					buttonVariants({ variant: "outline" }),
					"flex size-7 items-center justify-center bg-transparent opacity-50 hover:opacity-100",
				)}
			>
				<ChevronRight className="size-4" />
			</button>
		</div>
	);
}

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: CalendarProps) {
	const [month, setMonthState] = React.useState<Date>(
		props.defaultMonth ?? new Date(),
	);

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			month={month}
			onMonthChange={setMonthState}
			className={cn("p-3", className)}
			hideNavigation
			classNames={{
				months: "flex flex-col sm:flex-row gap-2",
				month: "flex flex-col gap-4",
				month_caption: "flex items-center",
				caption_label: "hidden",
				month_grid: "w-full border-collapse space-x-1",
				weekdays: "flex",
				weekday:
					"text-(--color-muted-foreground) rounded-(--radius-xs) w-8 font-normal text-[0.8rem]",
				week: "flex w-full mt-2",
				day: cn(
					"relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-(--color-primary-100) [&:has([aria-selected].day-outside)]:bg-(--color-primary-100)/50 [&:has([aria-selected].day-range-end)]:rounded-r-(--radius-xs)",
					props.mode === "range"
						? "[&:has(>.day-range-end)]:rounded-r-(--radius-xs) [&:has(>.day-range-start)]:rounded-l-(--radius-xs) first:[&:has([aria-selected])]:rounded-l-(--radius-xs) last:[&:has([aria-selected])]:rounded-r-(--radius-xs)"
						: "[&:has([aria-selected])]:rounded-(--radius-xs)",
				),
				day_button: cn(
					buttonVariants({ variant: "ghost" }),
					"size-8 p-0 font-normal aria-selected:opacity-100",
				),
				range_end: "day-range-end",
				range_start: "day-range-start",
				selected:
					"bg-(--color-primary) text-(--color-primary-foreground) hover:bg-(--color-primary) hover:text-(--color-primary-foreground) focus:bg-(--color-primary) focus:text-(--color-primary-foreground)",
				today: "bg-(--color-muted) text-(--color-foreground)",
				outside:
					"day-outside text-(--color-muted-foreground) aria-selected:text-(--color-muted-foreground)",
				disabled: "text-(--color-muted-foreground) opacity-50",
				range_middle:
					"aria-selected:bg-(--color-muted) aria-selected:text-(--color-foreground)",
				hidden: "invisible",
				...classNames,
			}}
			components={{
				MonthCaption: () => (
					<MonthYearDropdowns
						displayMonth={month}
						onMonthChange={setMonthState}
						onPreviousMonth={() => setMonthState(subMonths(month, 1))}
						onNextMonth={() => setMonthState(addMonths(month, 1))}
					/>
				),
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
